import { useState } from "react";

import { Close, Search } from "@mui/icons-material";
import { Drawer, FormControlLabel, IconButton, InputAdornment, Pagination, Switch, TextField } from "@mui/material";
import { CustomChip } from "components/CustomChip";
import { useSearchParams } from "react-router-dom";
import { useDebounceValue } from "usehooks-ts";
import { getPageCount, getCurrentPageData } from "utils/pagination";

import Comment from "./Comment";
import DownloadExcel from "../../components/DownloadExcel";
import { COMMENTS_TRENDS } from "../../constants";
import { getCommentsDownloadData } from "../../utils";

const favorabilityOptions = ["All", "Favorable", "Unfavorable", "Neutral", "What Else Question"];
const sentimentOptions = ["All", "positive", "negative", "neutral"];

const ITEM_PER_PAGE = 20;

export const CommentDrawerWrapper = ({ comments, theme, component, favouriteComments = [], allComments = [] }) => {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [commentFilters, setCommentFilters] = useState({
    favoribilty: "All",
    sentiment: "All",
    bookmarks: false,
  });
  const [likedComments, setLikedComments] = useState(() => new Set(favouriteComments));
  const [currentParams] = useSearchParams();
  const trend = currentParams.get("trend") || COMMENTS_TRENDS.FAVORABILITY;
  const [searchQuery, setSearchQuery] = useDebounceValue("", 500);
  const drawerComments = [...comments].sort((a, b) => {
    // If either comment has a distance value, sort by distance
    if (a.distance || b.distance) {
      // Most relevant comments have least distance and should come first
      // comments without distance are considered least relevant
      return (a.distance ?? 1) - (b.distance ?? 1);
    } else {
      // otherwise, sort by comment length (longer comments first)
      return b.comment?.length - a.comment?.length;
    }
  });

  const favorabilityCounts = comments.reduce((acc, comment) => {
    if (["Favorable", "Unfavorable", "Neutral"].includes(comment.favorability)) {
      acc[comment.favorability] = (acc[comment.favorability] || 0) + 1;
    } else {
      acc["What Else Question"] = (acc["What Else Question"] || 0) + 1;
    }
    return acc;
  }, {});

  const sentimentCounts = comments.reduce((acc, comment) => {
    if (["positive", "negative", "neutral"].includes(comment.sentiment)) {
      acc[comment.sentiment] = (acc[comment.sentiment] || 0) + 1;
    } else {
      acc["N/A"] = (acc["N/A"] || 0) + 1;
    }
    return acc;
  }, {});

  favorabilityCounts["All"] = comments.length;
  sentimentCounts["All"] = comments.length;

  const filteredComments = drawerComments.filter((comment) => {
    const isLiked = likedComments.has(comment.comment_id);

    if (commentFilters.bookmarks && !isLiked) {
      return false;
    }

    if (searchQuery && !comment.comment.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }

    return trend === COMMENTS_TRENDS.FAVORABILITY ? (
      commentFilters.favoribilty === "All"
      || (commentFilters.favoribilty === "What Else Question" && !["Favorable", "Unfavorable", "Neutral"].includes(comment.favorability)) 
      || comment.favorability === commentFilters.favoribilty
    ) : (
      commentFilters.sentiment === "All"
      || (commentFilters.sentiment === "N/A" && !["positive", "negative", "neutral"].includes(comment.sentiment)) 
      || comment.sentiment === commentFilters.sentiment
    );
  });

  const pageComments = getCurrentPageData(filteredComments, ITEM_PER_PAGE, page);

  const handleDrawerClose = (e, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      setOpen(false);
    }
  };

  return <div className="h-full">
    <div onClick={() => setOpen(true)} className="h-full">{component}</div>
    <Drawer anchor="right" open={open} onClose={handleDrawerClose}>
      <div className="w-[900px] px-8 pb-8" role="presentation">
        <div className="sticky top-0 bg-white z-10 pt-8">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-lg font-bold">{theme ? "Relevant" : "All"} comments</h2>
              {theme ? <p>{theme}</p> : null}
            </div>
            <div>
              <DownloadExcel data={getCommentsDownloadData(allComments)} label="All comments" />
              <IconButton onClick={() => setOpen(false)}>
                <Close />
              </IconButton>
            </div>
          </div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              {trend === COMMENTS_TRENDS.FAVORABILITY ? favorabilityOptions.map((option) => {
                return <CustomChip
                  key={option}
                  size="small"
                  label={`${option} - ${favorabilityCounts[option] || 0}`}
                  count={favorabilityCounts[option]}
                  isActive={commentFilters.favoribilty === option}
                  onClick={() => setCommentFilters({ ...commentFilters, favoribilty: option })}
                />;
              }): sentimentOptions.map((option) => {
                return <CustomChip
                  key={option}
                  size="small"
                  label={`${option} - ${sentimentCounts[option] || 0}`}
                  count={sentimentCounts[option]}
                  isActive={commentFilters.sentiment === option}
                  onClick={() => setCommentFilters({ ...commentFilters, sentiment: option })}
                />;
              })}
            </div>
            <FormControlLabel control={<Switch value={commentFilters.bookmarks} onChange={(e) => {
              setCommentFilters({ ...commentFilters, bookmarks: Boolean(e.target.checked) });
            }} />} label="Bookmarks only" />
          </div>
          <TextField
            type="text"
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search for a keyword or a phrase"
            sx={{ width: "100%" }}
            InputProps={{
              sx: {
                borderRadius: "5px",
              },
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </div>
        {pageComments.map((comment) => {
          return <Comment key={comment.comment_id} comment={comment} highlight={searchQuery} favorite={favouriteComments.includes(comment.comment_id)} onLikeChange={(comment_id, liked) => {
            setLikedComments((prev) => {
              const newSet = new Set(prev);
              if (liked) {
                newSet.add(comment_id);
              } else {
                newSet.delete(comment_id);
              }
              return newSet;
            });
          }} />;
        })}
        {!commentFilters.bookmarks && filteredComments.length > 0 ? (
          <div className="mt-4 flex justify-center">
            <Pagination
              count={getPageCount(filteredComments.length, ITEM_PER_PAGE)} 
              variant="outlined" 
              shape="rounded" 
              onChange={(e, page) => setPage(page)}
            />
          </div>
        ): null}
      </div>
    </Drawer>
  </div>;
};
